import {
  type MotionValue,
  m,
  useIsPresent,
  useReducedMotion,
} from "motion/react";

import { ResizeHandleButton } from "src/base-components/ResizableSidepane";
import { UnifiedCopilotChat } from "src/unifiedCopilot/chat/UnifiedCopilotChat";
import { UnifiedCopilotHeader } from "src/unifiedCopilot/UnifiedCopilotHeader";

type UnifiedCopilotSidebarProps = {
  animatedWidth: MotionValue<number>;
  targetWidth: number;
  isResizing: boolean;
  onResizeStart: () => void;
};

export const UnifiedCopilotSidebar: React.FC<UnifiedCopilotSidebarProps> = ({
  animatedWidth,
  targetWidth,
  isResizing,
  onResizeStart,
}) => {
  const reduceMotion = useReducedMotion();
  const isPresent = useIsPresent();

  return (
    <m.div
      animate={{ width: targetWidth }}
      className="shrink-0 overflow-hidden"
      exit={{
        width: 0,
        transition: { duration: reduceMotion ? 0 : 0.22, ease: [0.4, 0, 1, 1] },
      }}
      initial={{ width: 0 }}
      style={{ width: animatedWidth }}
      transition={{
        duration: isResizing || reduceMotion ? 0 : 0.28,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <m.aside
        animate={{ opacity: 1, x: 0 }}
        initial={{ opacity: reduceMotion ? 1 : 0, x: reduceMotion ? 0 : 24 }}
        exit={{
          opacity: 0,
          x: reduceMotion ? 0 : 16,
          transition: { duration: reduceMotion ? 0 : 0.14, delay: 0 },
        }}
        transition={{
          duration: reduceMotion ? 0 : 0.22,
          ease: [0.22, 1, 0.36, 1],
        }}
        inert={!isPresent}
        aria-label="Taktile Agent"
        className="relative flex h-full max-h-screen flex-col border-gray-200 border-l bg-white"
        data-loc="unified-copilot-sidebar"
        style={{ width: targetWidth }}
      >
        <ResizeHandleButton onResizeStart={onResizeStart} />
        <UnifiedCopilotHeader surface="sidebar" />
        <m.div
          animate={{ opacity: 1, y: 0 }}
          initial={{ opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 8 }}
          transition={{
            duration: reduceMotion ? 0 : 0.24,
          }}
          className="min-h-0 flex-1"
        >
          <UnifiedCopilotChat />
        </m.div>
      </m.aside>
    </m.div>
  );
};
